import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Calendar, MapPin, Stethoscope, Archive, ArchiveRestore } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api } from "../lib/api";
import { Page, Spinner, StatusChip, UrgencyChip, Chip } from "../components/Brand";
import { formatDateFr, timeAgoFr, add30 } from "../lib/fr";

export default function ReferralDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: ref, isLoading } = useQuery({
    queryKey: ["referral", id],
    queryFn: async () => (await api.get(`/referrals/${id}`)).data,
  });

  if (isLoading) return <Page><div className="cs-card p-10 flex justify-center"><Spinner /></div></Page>;
  if (!ref) return <Page><div>Référence introuvable</div></Page>;

  async function archive() {
    if (!window.confirm("Archiver cette référence ? Elle ne sera plus affichée par défaut.")) return;
    try {
      await api.post(`/referrals/${id}/archive`);
      toast.success("Référence archivée");
      queryClient.invalidateQueries({ queryKey: ["my-referrals"] });
      setTimeout(() => navigate("/im"), 500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  }

  async function unarchive() {
    try {
      await api.post(`/referrals/${id}/unarchive`);
      toast.success("Référence restaurée");
      queryClient.invalidateQueries({ queryKey: ["referral", id] });
      queryClient.invalidateQueries({ queryKey: ["my-referrals"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  }

  return (
    <Page>
      <Toaster position="top-center" richColors />
      <button onClick={() => navigate(-1)} className="text-[14px] mb-4 inline-flex items-center gap-1 hover:underline"
              style={{ color: "var(--cs-text-muted)" }} data-testid="back-link">
        <ChevronLeft size={16} /> Retour
      </button>

      <section className="cs-card p-5 md:p-6" data-testid="referral-detail">
        <div className="flex items-start justify-between pb-4 border-b" style={{ borderColor: "var(--cs-border)" }}>
          <div>
            <div className="cs-label">Référence</div>
            <div className="font-semibold mt-1" style={{ fontSize: 28, letterSpacing: "-0.5px" }}>{ref.referral_code}</div>
            <div className="text-[12px] mt-1" style={{ color: "var(--cs-text-subtle)" }}>
              Créée {timeAgoFr(ref.created_at)}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <UrgencyChip value={ref.urgency} />
            <StatusChip value={ref.status} />
          </div>
        </div>

        {ref.appointment_date && (
          <div className="mt-5 p-4 rounded-[12px]" style={{ background: "rgba(124,179,66,0.10)", borderLeft: "4px solid var(--cs-primary)" }}>
            <div className="cs-label" style={{ color: "var(--cs-primary-dark)" }}>Rendez-vous programmé</div>
            <div className="font-semibold mt-2 text-[16px]"><Calendar size={14} className="inline mr-1" /> {formatDateFr(ref.appointment_date)}</div>
            <div className="text-[14px] mt-0.5" style={{ color: "var(--cs-text)" }}>{ref.appointment_time} → {add30(ref.appointment_time)} (30 min)</div>
            <div className="text-[13px] mt-2 flex flex-wrap gap-3" style={{ color: "var(--cs-text-muted)" }}>
              <span><MapPin size={12} className="inline" /> {ref.location}</span>
              <span><Stethoscope size={12} className="inline" /> {ref.provider}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-5 pt-5 text-[14px]">
          <Field label="Diagnostic" value={ref.diagnosis === "cardiomyopathy" ? "Cardiomyopathie" : "CMP péripartum"} />
          <Field label="FEVG" value={ref.ef_range + " %"} />
          <Field label="Tranche d'âge" value={ref.age_range + " ans"} />
          <Field label="Grossesse" value={pregLabel(ref.pregnancy_status)} />
          <Field label="Contraception actuelle" value={contraLabel(ref.current_contraception)} />
          <Field label="Plan envisagé" value={contraLabel(ref.contraception_plan)} />
          <Field label="Œstrogènes" value={ref.estrogen_contraindicated ? "Contre-indiqués" : "Autorisés"} />
        </div>

        {ref.notes && (
          <div className="mt-5 p-4 rounded-[12px] text-[13px]" style={{ background: "var(--cs-bg)" }}>
            <div className="cs-label mb-1.5">Notes</div>
            {ref.notes}
          </div>
        )}

        <div className="mt-6 pt-5 border-t flex flex-wrap gap-3" style={{ borderColor: "var(--cs-border)" }}>
          {ref.archived ? (
            <>
              <Chip tone="muted">Archivée</Chip>
              <button onClick={unarchive} className="cs-btn-ghost text-[13px]" data-testid="unarchive-button">
                <ArchiveRestore size={14} /> Restaurer
              </button>
            </>
          ) : (
            <button onClick={archive} className="cs-btn-danger-outline text-[13px]" data-testid="archive-button">
              <Archive size={14} /> Archiver la référence
            </button>
          )}
        </div>
      </section>
    </Page>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="cs-label">{label}</div>
      <div className="font-medium mt-0.5">{value}</div>
    </div>
  );
}
function pregLabel(v) { return ({ not_pregnant: "Non enceinte", pregnant_t1: "T1", pregnant_t2: "T2", pregnant_t3: "T3", postpartum: "Post-partum" })[v] || v; }
function contraLabel(v) {
  return ({
    none: "Aucune", condom: "Préservatif", copper_iud: "DIU cuivre", hormonal_iud: "DIU hormonal",
    implant: "Implant", progestin_pill: "Pilule progestative", combined_pill: "Pilule combinée",
    dmpa: "DMPA", sterilization: "Stérilisation", none_yet: "À déterminer", permanent: "Méthode permanente",
  })[v] || v;
}
